'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Typography from '@alltrails/core/components/Typography';
import useStateRef from '@alltrails/core/hooks/useStateRef';
import useLocalStorage from '@alltrails/local-storage/useLocalStorage';
import { canvasHeight, canvasWidth } from './constants';
import styles from './styles.module.scss';
import useBackgroundRef from './useBackgroundRef';
import useGameScoreRefs from './useGameScoreRefs';
import useHikerRef from './useHikerRef';
import useJumpControls from './useJumpControls';
import useObstaclesRef from './useObstaclesRef';

const maxFrameDeltaMs = 1000 / 30; // 30 FPS
const totalComponentCount = 3;
const hikerGameHighScoreKey = 'hiker-game-high-score';

type HikerGameProps = {
  debugMode?: boolean;
};

const HikerGame = ({ debugMode = false }: HikerGameProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [loadedComponentsCount, setLoadedComponentsCount] = useState(0);
  const onComponentLoad = useMemo(() => () => setLoadedComponentsCount(prev => prev + 1), []);
  const isGameLoaded = loadedComponentsCount === totalComponentCount;

  const [isGameRunning, setIsGameRunning] = useState(false);
  const [isGameOver, setIsGameOver, isGameOverRef] = useStateRef(false);

  const { getValue: getHighScore, setValue: setHighScore } = useLocalStorage<number>(hikerGameHighScoreKey);
  const [scoreElementRef, scoreTrackerRef] = useGameScoreRefs();

  const onCollision = useMemo(
    () => () => {
      setIsGameRunning(false);
      setIsGameOver(true);

      const highScore = getHighScore() ?? 0;
      if (scoreTrackerRef.current.value > highScore) {
        setHighScore(scoreTrackerRef.current.value);
      }
    },
    [getHighScore, scoreTrackerRef, setHighScore, setIsGameOver]
  );

  const backgroundRef = useBackgroundRef(onComponentLoad);
  const hikerRef = useHikerRef(onComponentLoad, debugMode);
  const obstaclesRef = useObstaclesRef(onComponentLoad, onCollision, debugMode);

  const resetCanvas = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      // Draw the initial state of the game
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      backgroundRef.current.draw(ctx, 0);
      hikerRef.current.reset();
      hikerRef.current.draw(ctx, 0);
      obstaclesRef.current.reset();
      obstaclesRef.current.draw(ctx, 0);
      scoreTrackerRef.current.reset();
    }
  }, [backgroundRef, hikerRef, obstaclesRef, scoreTrackerRef]);

  useEffect(() => {
    if (!isGameLoaded) {
      return;
    }
    resetCanvas();
    // Only want to run this once, when the game is first loaded
  }, [isGameLoaded]);

  useEffect(() => {
    // Handle keyboard events before the game is ever running
    if (isGameLoaded && !isGameRunning && !isGameOver) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === 'Space' && !event.repeat) {
          event.preventDefault();
          event.stopPropagation();
          setIsGameRunning(true);
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return () => {};
  }, [isGameLoaded, isGameOver, isGameRunning, resetCanvas, setIsGameRunning, setIsGameOver]);

  useEffect(() => {
    // Handle keyboard events when the game is over
    if (isGameOver) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === 'Space' && !event.repeat) {
          event.preventDefault();
          event.stopPropagation();
          resetCanvas();
          setIsGameOver(false);
        }
      };

      // Delay adding the keydown listener to avoid users from accidentally restarting the game by trying to jump as the game ends
      setTimeout(() => {
        document.addEventListener('keydown', handleKeyDown);
      }, 1000);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return () => {};
  }, [isGameOver, resetCanvas, setIsGameOver]);

  useJumpControls(hikerRef, isGameRunning);

  useEffect(() => {
    // Handle game updates when the game is running
    if (!isGameRunning) {
      return;
    }

    let lastFrameTime = performance.now();
    function drawFrame(now: number) {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx || isGameOverRef.current) {
        return;
      }

      // Maintain a consistent update rate based on time delta rather than frame rate.
      // Protect against FPS spikes by limiting the time delta to the maximum allowed frame delta.
      const msDelta = Math.min(now - lastFrameTime, maxFrameDeltaMs);
      lastFrameTime = now;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      scoreTrackerRef.current.incrementScore(msDelta);
      backgroundRef.current.draw(ctx, msDelta);
      hikerRef.current.draw(ctx, msDelta);
      obstaclesRef.current.draw(ctx, msDelta);
      obstaclesRef.current.detectCollision(hikerRef.current.getHitBoxes());
      requestAnimationFrame(drawFrame);
    }

    requestAnimationFrame(drawFrame);
    // Only want to run this once, when the game starts running
  }, [isGameRunning]);

  const instructionText = isGameRunning ? (
    <>
      Press SPACE to jump
      <br />
      Hold SPACE to jump higher
    </>
  ) : isGameOver ? (
    <>
      Press SPACE to restart
      <br />
      <span>Game over!</span>
    </>
  ) : (
    'Press SPACE to start'
  );

  return (
    <>
      <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" height={canvasHeight} width={canvasWidth} />
      {isGameLoaded && (
        <>
          <Typography className={classNames(styles.instructions, { [styles.fadeOut]: isGameRunning })} variant="text200bold">
            {instructionText}
          </Typography>
          <Typography className={styles.highScore} variant="text200bold">
            High score: {getHighScore() ?? 0}
          </Typography>
          <div className={styles.score} ref={scoreElementRef} />
        </>
      )}
    </>
  );
};

export default HikerGame;
